import axios from 'axios';

// Default API base. The app is served two ways and the "/api/v1" prefix is
// resolved by the SERVING layer in each case, never baked into the bundle:
//   - direct  lambda2:3001 (root)        -> this image's nginx `location /api`
//     proxies "/api/v1/*" straight to the backend.
//   - public  orbital-laser.com/land-ai/ -> the upstream reverse proxy runs a
//     sub_filter that rewrites every "/api/v1" string in the served JS body to
//     "/land-ai/api/v1", so a bare "/api/v1" here goes out as the proxied
//     "/land-ai/api/v1" on the wire.
// The bundle MUST therefore emit a BARE "/api/v1" (matching every hardcoded
// fetch("/api/v1/...") elsewhere in the app). Do NOT prepend getBasePath()
// here: on the public deploy the sub_filter already injects "/land-ai", so a
// runtime prefix double-applies it ("/land-ai" + "/land-ai/api/v1") and every
// axios call lands on the SPA HTML shell instead of the API — breaking REST and
// driving the idle URL-churn flicker (empirically confirmed 2026-07-04: the
// public bundle carries 23 sub_filter rewrites the direct bundle does not).
const DEFAULT_API_BASE = '/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || DEFAULT_API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ----------------------------------------------------------------------
// Separate axios instance for posting diagnostics back to the backend.
// MUST NOT share interceptors with `apiClient`: if a diag POST itself
// failed it would re-enter the interceptor that posted it and spawn an
// infinite loop. This client is bare-bones, has no auth header, no
// retry, no error interceptor -- every diag POST is fire-and-forget.
// ----------------------------------------------------------------------
const diagClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || DEFAULT_API_BASE,
  timeout: 5000,
  headers: { 'Content-Type': 'application/json' },
});

// ----------------------------------------------------------------------
// Diagnostic instrumentation for the recurrent "Could not connect" /
// "Network Error" pattern (2026-05-30 / 2026-05-31).
// ----------------------------------------------------------------------
// Background: Safari intermittently reports axios "Network Error" with
// no useful detail. Server-side curl + logs show the requests aren't
// even reaching the server. Hard to debug without seeing what the
// browser actually attempted at the failure moment.
//
// This block captures a rolling buffer of every request + every failure
// with full timing + response-header detail, pushes it to a global
// `window.__apiDiag__` so you can paste it from Web Inspector when the
// failure repros. Also auto-logs the last 5 failures to console with a
// clearly-labelled marker so they're easy to copy out.
//
// Strictly dev-mode safe — no network calls, no PII, just timing + URLs
// + status codes. Disable by setting `localStorage.api_diag = 'off'`.
const DIAG_BUFFER_MAX = 100;
const DIAG_ENABLED = () => {
  try { return localStorage.getItem('api_diag') !== 'off'; }
  catch (_) { return true; }
};
if (typeof window !== 'undefined') {
  window.__apiDiag__ = window.__apiDiag__ || {
    requests: [],       // rolling
    failures: [],       // rolling
    sessionStartedAt: new Date().toISOString(),
    dumpFailures: () => {
      // eslint-disable-next-line no-console
      console.log('=== api_diag failures ===');
      // eslint-disable-next-line no-console
      console.table(window.__apiDiag__.failures);
    },
    dumpAll: () => {
      // eslint-disable-next-line no-console
      console.log(JSON.stringify(window.__apiDiag__, null, 2));
    },
  };
}
const _diagPush = (kind, entry) => {
  if (!DIAG_ENABLED() || typeof window === 'undefined') return;
  const arr = window.__apiDiag__[kind];
  arr.push(entry);
  if (arr.length > DIAG_BUFFER_MAX) arr.shift();
};

// Fire-and-forget POST of a failure record to the backend diag sink
// (`POST /api/v1/diag/client-error`, see backend/src/landai/api/v1/diag.py).
// MUST NEVER throw and MUST NOT re-trigger the failure capture path,
// otherwise a broken diag endpoint would loop the browser. Guards:
//   * Uses `diagClient` (no auth/no interceptors) so the apiClient
//     response interceptor never sees this request.
//   * Skips the POST entirely if the failed request was already
//     targeting /diag/client-error (defensive: the apiClient
//     interceptor today shouldn't see those requests because they go
//     through diagClient, but if something else routes them through
//     apiClient we still don't want to recurse).
//   * Wrapped in try/catch + a .catch on the returned promise so any
//     network/JS error here is swallowed.
const _diagPostFailure = (failure, requestUrl) => {
  if (typeof window === 'undefined') return;
  if (!DIAG_ENABLED()) return;
  try {
    const url = String(requestUrl || '');
    if (url.includes('/diag/client-error')) return;
    const body = {
      ...failure,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      pageUrl: typeof window !== 'undefined' && window.location
        ? window.location.href
        : null,
    };
    // Fire-and-forget. `.catch` here swallows network errors so the
    // unhandled-rejection logger never sees them. We deliberately do
    // NOT await -- caller is in an axios error interceptor and must
    // continue rejecting the original error promptly.
    diagClient.post('/diag/client-error', body).catch(() => {});
  } catch (_) {
    /* never throw from diagnostics */
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Diagnostic: stamp start time on the config so we can measure
    // duration in the response/error interceptor below.
    config._diagStartTs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    config._diagId = `${config.method || 'get'}:${config.url}:${Math.random().toString(36).slice(2, 7)}`;
    _diagPush('requests', {
      id: config._diagId,
      t: new Date().toISOString(),
      method: config.method,
      url: config.url,
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ----------------------------------------------------------------------
// authHeaders(): Bearer header for raw fetch() calls that do NOT flow
// through apiClient (and therefore miss the request interceptor above) —
// the streaming/polling research card plus the neighbors / owner-portfolio
// / thesis_tags / adjacent / similar / transcript cards in ParcelDetail
// and ReportViewer. App-wide login enforcement (#280 / #944) gated the
// parcels + zip_research routers, so those tokenless fetches now 401.
// Mirrors the interceptor's token read; returns {} when logged out.
// ----------------------------------------------------------------------
export function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Single auto-retry for idempotent GETs that timed out or hit a
    // transient network error. Backend rebuilds (~10-20s cold start)
    // were causing user-visible "Failed to load boundaries" cascades
    // because every in-flight GET hit its 30s timer at the same moment.
    // We give it one shot to recover before surfacing the error.
    const cfg = error.config || {};
    const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
    const isNetwork = !error.response && error.message === 'Network Error';
    const isGet = (cfg.method || 'get').toLowerCase() === 'get';

    // Diagnostic capture for the recurrent Safari "Could not connect"
    // pattern — record everything we know about this failure before any
    // retry or auth dispatch consumes the error object.
    //
    // Skip ERR_CANCELED: that's the AbortController firing
    // intentionally (e.g. map-pan cancels the previous /parcels/points
    // request before the new one fires). Treating those as failures
    // floods the diag pipeline with non-actionable noise — observed
    // 2026-05-31 where 14 ERR_CANCELED rows landed in client_diag_events
    // from normal map panning.
    const isCanceled = error.code === 'ERR_CANCELED'
      || error.code === 'CanceledError'
      || error.name === 'CanceledError'
      || error.message === 'canceled';
    if (!isCanceled) {
      const startTs = cfg._diagStartTs;
      const elapsedMs = startTs != null
        ? ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTs)
        : null;
      const failure = {
        id: cfg._diagId,
        t: new Date().toISOString(),
        method: cfg.method,
        url: cfg.url,
        elapsedMs: elapsedMs != null ? Math.round(elapsedMs) : null,
        status: error.response?.status ?? null,
        statusText: error.response?.statusText ?? null,
        // axios error codes: ECONNABORTED (timeout), ERR_NETWORK
        // (Safari "could not connect" lands here), ERR_BAD_RESPONSE,
        // etc. The most diagnostic field for the current bug.
        code: error.code ?? null,
        message: error.message ?? null,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        retried: !!cfg._retried,
        // request.readyState if XHR transport — Safari sometimes
        // surfaces 0 (unsent) or 4 (done) with no body; helps tell
        // socket-aborted-mid-flight vs never-sent.
        xhrReadyState: error.request?.readyState ?? null,
        // Response headers can include the dreaded `Connection: close`
        // we observed at the server side. Capture them when available.
        responseHeaders: error.response?.headers
          ? Object.fromEntries(Object.entries(error.response.headers))
          : null,
      };
      _diagPush('failures', failure);
      // Loud console log when DIAG is enabled so a copy-paste from
      // Inspector immediately surfaces actionable evidence.
      if (DIAG_ENABLED()) {
        // eslint-disable-next-line no-console
        console.warn('[api_diag failure]', failure);
      }
      // Pipe to backend sink for cross-session aggregation and so
      // the owner can monitor failures without needing the browser
      // open. Fire-and-forget; never throws, never retries.
      _diagPostFailure(failure, cfg.url);
    }

    if (isGet && (isTimeout || isNetwork) && !cfg._retried) {
      cfg._retried = true;
      // Brief backoff so a still-restarting backend has a chance.
      await new Promise(r => setTimeout(r, 1500));
      return apiClient.request(cfg);
    }

    if (error.response) {
      // Handle specific error codes
      if (error.response.status === 401) {
        localStorage.removeItem('auth_token');
        // Don't redirect from login/register itself (would cause a loop) or
        // from the public /auth/me probe that AuthContext runs on mount to
        // validate any stored token. Let those paths handle the 401 locally.
        const path = window.location.pathname;
        const isAuthProbe = error.config?.url?.includes('/auth/me');
        const onPublicPage = path === '/login' || path === '/register' || path === '/';
        if (!isAuthProbe && !onPublicPage) {
          // Dispatch a custom event instead of window.location.href so the
          // redirect uses React Router (no full page reload → auth state
          // stays coherent). AuthContext listens for this event and calls
          // navigate(). See #173.
          window.dispatchEvent(
            new CustomEvent('auth:unauthorized', { detail: { returnPath: path } }),
          );
        }
      }
      if (error.response.status === 404) {
        throw new Error(`Not found`);
      }
    }
    return Promise.reject(error);
  }
);

// Parcel endpoints
export const searchParcels = async (params = {}) => {
  const response = await apiClient.get('/parcels', { params });
  return response.data;
};

// Bbox-area cap mirroring the server-side guard added in PR-806.
// Issue #807: the default world-view bbox is ~345 deg^2 and used to hang for
// 30s before the server cap was added. Without any narrowing filters we
// keep the strict 100 deg² cap; with narrowing filters (state, county, q,
// is_listed=true) the index-driven plan is fast even at CONUS scale, so we
// raise the cap to 2000 deg² so a zoomed-out map with a tight filter still
// renders instead of stalling behind a "zoom in" overlay.
export const PARCEL_POINTS_BBOX_CAP_DEG2 = 100;
export const PARCEL_POINTS_BBOX_CAP_DEG2_FILTERED = 2000;

// Only treat index-backed predicates as narrowing. state/county/is_listed=true
// each drive an Index Scan that reduces the bbox check to a recheck. q
// (ILIKE) seq-scans the parcels table even at LIMIT and would re-introduce
// the 30s hang the cap was originally meant to prevent.
const hasNarrowingFilters = (filters) =>
  Boolean(
    filters &&
      (filters.state ||
        (filters.county && String(filters.county).length >= 2) ||
        filters.is_listed === true),
  );

export const isBboxWithinFetchCap = (bbox, filters) => {
  if (typeof bbox !== 'string') return false;
  const parts = bbox.split(',').map(Number);
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return false;
  const [west, south, east, north] = parts;
  const w = Math.max(0, east - west);
  const h = Math.max(0, north - south);
  const cap = hasNarrowingFilters(filters)
    ? PARCEL_POINTS_BBOX_CAP_DEG2_FILTERED
    : PARCEL_POINTS_BBOX_CAP_DEG2;
  return w * h <= cap;
};

export const getParcelPoints = async (bbox, filters = {}, limit = 5000, { signal } = {}) => {
  if (!isBboxWithinFetchCap(bbox, filters)) {
    // Don't make the call — server will 400 anyway and the empty result
    // looks identical to "no parcels found" in this region. Return a
    // sentinel so callers can render the right UX.
    return { points: [], count: 0, bbox_too_large: true };
  }
  const params = { bbox, limit, ...filters };
  const response = await apiClient.get('/parcels/points', { params, signal });
  return response.data;
};

// The bare APN is not an identity (#1281, #1292): the same string names
// different parcels in different counties, and the backend resolves a
// bare APN to the lowest row id. `params.state` / `params.county` narrow
// the lookup to the parcel the caller meant — `GET /parcels/{id}` already
// accepts both. They are optional and omitted when absent, so a link
// without them keeps working (the detail page then shows the
// disambiguation picker).
//
// The score endpoints take the same pair: the score tables are keyed by
// the APN string, so without the disambiguator a shared APN can serve a
// colliding twin's numbers.
export const scopeParams = (params = {}) => {
  const scoped = {};
  if (params.state) scoped.state = params.state;
  if (params.county) scoped.county = params.county;
  return scoped;
};

export const getParcel = async (parcelId, params = {}) => {
  const response = await apiClient.get(`/parcels/${parcelId}`, {
    params: scopeParams(params),
  });
  return response.data;
};

export const getParcelBoundaries = async (bbox, filters = {}, limit = 500, { signal } = {}) => {
  const params = { bbox, limit, ...filters };
  const response = await apiClient.get('/parcels/boundaries', { params, signal });
  return response.data;
};

// Deep (max) property record — sourced public-records dossier for a parcel
// (property-record-maximizer read path, #1059). Returns { has_max_record,
// n_datapoints, n_sources, sources[], datapoints[] }; has_max_record=false
// with empty arrays when nothing has been ingested for the parcel yet.
export const getParcelMaxRecord = async (parcelId) => {
  const response = await apiClient.get(`/parcels/${parcelId}/max-record`);
  return response.data;
};

// On-demand deep research (#1059) — enqueue the property-record
// maximizer job for a parcel (any authenticated user, FULL depth).
// The backend dedups, so callers must handle three response shapes:
//   - fresh enqueue (202):  { job_id, status: 'queued', queue_position? }
//   - active job (200):     { job_id, status: 'queued'|'running', ... }
//   - fresh record (#1107): { job_id: null, status: 'done', cached: true,
//                             record_id, n_datapoints } — NOT an error;
//                             the caller should render the existing record.
// Pass { force: true } (the "Re-run research" button) to bypass the
// fresh-record dedup so the engine actually re-runs — e.g. to pick up an
// engine improvement. The initial/auto call omits it so normal navigation
// still dedups to the cached record. The active-job dedup + per-user quota
// still apply, so force cannot double-enqueue or spam.
export const startDeepResearch = async (parcelId, { force = false } = {}) => {
  const response = await apiClient.post(
    `/parcels/${parcelId}/deep-research`,
    null,
    force ? { params: { force: true } } : undefined,
  );
  return response.data;
};

// Poll a research job's live status (#1059). Returns
// { job_id, parcel_id, status(queued|running|done|failed), progress,
// activity, n_datapoints, error? }. Paired with getParcelMaxRecord so
// the research view can render the datapoints accumulating in real time.
export const getResearchJob = async (jobId) => {
  const response = await apiClient.get(`/research-jobs/${jobId}`);
  return response.data;
};

// Research Library (#1059) — list past deep-research records. `mine`
// scopes to records the current user requested; otherwise all records
// (research on a parcel helps everyone) are returned. Each row:
// { parcel_id, apn, county, state, generated_at, data_point_count,
// source_count, target_met }.
export const listResearchLibrary = async ({ mine = false, limit = 100 } = {}) => {
  const response = await apiClient.get('/research-library', {
    params: { mine, limit },
  });
  return response.data;
};

// In-progress deep-research jobs (#1097). Complements listResearchLibrary,
// which only returns *completed* records. Returns { count, jobs: [{ job_id,
// parcel_id, apn, county, state, status(queued|running), queue_position,
// progress, activity, n_datapoints, created_at }] }. `mine` scopes to the
// current user's jobs; `parcelId` narrows to a single parcel so the streaming
// view can re-attach to a running job opened without a ?job= handoff.
export const listResearchJobs = async ({ mine = false, parcelId = null } = {}) => {
  const params = { mine };
  if (parcelId) params.parcel_id = parcelId;
  const response = await apiClient.get('/research-jobs', { params });
  return response.data;
};

// Score endpoints
export const getScores = async (parcelId, scope = {}) => {
  const response = await apiClient.get(`/scores/${parcelId}`, {
    params: scopeParams(scope),
  });
  return response.data;
};

// Feature endpoints
export const getFeatures = async (parcelId, scope = {}) => {
  const response = await apiClient.get(`/features/${parcelId}`, {
    params: scopeParams(scope),
  });
  return response.data;
};

// Bookmark endpoints
export const createBookmark = async (parcelId) => {
  const response = await apiClient.post('/bookmarks', { parcel_id: parcelId });
  return response.data;
};

export const listBookmarks = async () => {
  const response = await apiClient.get('/bookmarks');
  return response.data;
};

export const checkBookmark = async (parcelId) => {
  const response = await apiClient.get(`/bookmarks/${parcelId}`);
  return response.data;
};

export const deleteBookmark = async (parcelId) => {
  const response = await apiClient.delete(`/bookmarks/${parcelId}`);
  return response.data;
};

// Downvote (hide) endpoints — issue #124. Mirrors the bookmark
// helpers above. The map filters out downvoted parcels by default
// (or tints them red when "Show hidden" is checked), so the
// frontend keeps a Set of downvoted parcel_ids in memory using
// listDownvotes() at mount time.
export const createDownvote = async (parcelId, reason = null) => {
  const response = await apiClient.post(
    `/parcels/${encodeURIComponent(parcelId)}/downvote`,
    reason ? { reason } : {},
  );
  return response.data;
};

export const deleteDownvote = async (parcelId) => {
  const response = await apiClient.delete(
    `/parcels/${encodeURIComponent(parcelId)}/downvote`,
  );
  return response.data;
};

export const checkDownvote = async (parcelId) => {
  const response = await apiClient.get(
    `/parcels/${encodeURIComponent(parcelId)}/downvote`,
  );
  return response.data;
};

export const getDownvotes = async () => {
  const response = await apiClient.get('/downvotes');
  return response.data;
};

export const recordView = async (parcelId) => {
  const response = await apiClient.post(`/parcels/${encodeURIComponent(parcelId)}/view`);
  return response.data;
};

export const getViewedIds = async () => {
  const response = await apiClient.get('/views');
  return response.data;
};

// Mineral sites endpoints
export const getNearbyMines = async (parcelId, radiusKm = 50, limit = 20) => {
  const response = await apiClient.get(`/minerals/nearby/${parcelId}`, {
    params: { radius_km: radiusKm, limit },
  });
  return response.data;
};

export const getCommodityScores = async (parcelId, scope = {}) => {
  const response = await apiClient.get(`/commodity-scores/${parcelId}`, {
    params: scopeParams(scope),
  });
  return response.data;
};

export const getMineralPoints = async (bbox, commodity = null, limit = 5000, devStatus = null) => {
  const params = { bbox, limit };
  if (commodity) params.commodity = commodity;
  if (devStatus) params.dev_status = devStatus;
  const response = await apiClient.get('/minerals/points', { params });
  return response.data;
};

// Federal lands endpoints
// Debug-only: hazard tract centroids + raw scores. Powers the
// "Hazard tracts (debug)" overlay so we can sanity-check the
// heatmap against the underlying NRI data.
export const getHazardTractsDebug = async (bbox, layer = 'wildfire', metric = 'risk', limit = 2000) => {
  const response = await apiClient.get('/parcels/hazard-tracts-debug', {
    params: { bbox, layer, metric, limit },
  });
  return response.data;
};

// Universal parcels-table endpoint (#145). Supports arbitrary
// allowlisted sort columns, richer filters than top-opportunities,
// and a 500-row per_page ceiling so the new /properties UI can show
// useful densities. The `params` object is passed verbatim — pass
// only the keys you want filtered on; the backend handles missing
// keys as "no filter".
export const getParcelsTable = async (params = {}) => {
  const response = await apiClient.get('/parcels/table', { params });
  return response.data;
};

// Build the absolute CSV-export URL for the current filter/sort.
// Returned as a string so the caller can drop it into an <a href>
// or trigger a download via window.location. Mirrors the /table
// query params; the backend caps to `limit` rows (default 10K, max
// 100K) so even very loose filters return without timing out.
export const buildParcelsTableCsvUrl = (params = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    q.set(k, v);
  }
  // apiClient is axios with a configured baseURL; we have to mirror it
  // here because the browser needs an absolute URL for the download.
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/parcels/table.csv?${q.toString()}`;
};

// Authenticated CSV download (#280). /parcels/table.csv now sits behind
// the app-wide login gate, so it can no longer be fetched via a bare
// <a href> — the browser wouldn't attach the Authorization header and
// the request would 401. Pull the bytes through apiClient (whose
// request interceptor adds the Bearer token) as a Blob; the caller
// turns it into a client-side download. Same param semantics as
// buildParcelsTableCsvUrl: empty/null/undefined values are dropped.
export const downloadParcelsTableCsv = async (params = {}) => {
  const cleaned = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    cleaned[k] = v;
  }
  const response = await apiClient.get('/parcels/table.csv', {
    params: cleaned,
    responseType: 'blob',
  });
  return response.data;
};

// Viewport-scoped InSAR ground-motion overlay (#140).
// Returns up to `limit` parcels in the bbox with a non-NULL
// insar_displacement_mm_yr. Coverage spans the Western US (#1042):
// WA, OR, CA, NV, AZ, UT, ID, WY, CO and TX (~4.4M parcels as of
// 2026-07); parcels outside those mosaics have no sample.
//
// ``sort`` (optional, default 'magnitude' for back-compat):
//   - 'magnitude': ABS(velocity) DESC — keeps the biggest displacers
//     when limit clips, but hides the stable majority.
//   - 'stratified': samples roughly proportionally across stable /
//     mild / strong / extreme magnitude buckets so the rendered
//     overlay matches the true distribution. The InsarLayer overlay
//     defaults to this mode per issue #710 finding 6.
export const getInsarParcels = async (bbox, limit = 2000, sort = 'magnitude') => {
  const response = await apiClient.get('/parcels/insar-debug', {
    params: { bbox, limit, sort },
  });
  return response.data;
};

export const getFederalLandBoundaries = async (bbox, zoom) => {
  // zoom is optional; backend defaults to 6 if absent. Passing it
  // lets the server pick a finer simplification tolerance at high
  // zoom so polygon edges don't render rounded.
  const params = zoom != null ? { bbox, zoom } : { bbox };
  const response = await apiClient.get('/federal-lands/boundaries', { params });
  return response.data;
};

// Zoning endpoints
export const getZoningBoundaries = async (bbox, category = null) => {
  const params = { bbox };
  if (category) params.category = category;
  const response = await apiClient.get('/zoning/boundaries', { params });
  return response.data;
};

// Mining claims endpoints
export const getMiningClaimBoundaries = async (bbox, claimType = null, limit = 2000) => {
  const params = { bbox, limit };
  if (claimType) params.claim_type = claimType;
  const response = await apiClient.get('/mining-claims/boundaries', { params });
  return response.data;
};

export const getParcelMiningClaims = async (parcelId) => {
  const response = await apiClient.get(`/mining-claims/parcel/${parcelId}`);
  return response.data;
};

// Generation-sites endpoint (#997). Returns a GeoJSON FeatureCollection of
// built wind/solar/geothermal points within a bbox. Optional per-`tech`
// filter (wind | solar | geothermal). Mirrors getMiningClaimBoundaries —
// bbox + optional filter + limit, resolving to `response.data` so the
// caller reads `.features` directly. Backs the multi-tech map overlay.
export const getGenerationPoints = async (bbox, tech = null, limit = 5000, { signal } = {}) => {
  const params = { bbox, limit };
  if (tech) params.tech = tech;
  const response = await apiClient.get('/generation/points', { params, signal });
  return response.data;
};

// Transmission-lines endpoint (#991). Returns a GeoJSON FeatureCollection of
// HIFLD + OSM electric power transmission lines within a bbox. Each feature's
// `properties` carries a server-derived `color` / `weight` / `dashed` (from
// VOLT_CLASS_STYLE) so the client styles lines from one source of truth.
// Optional filters mirror the endpoint contract: `min_voltage` (kV float),
// `volt_class` (HIFLD class string), `line_type` (substring, e.g. 'AC'/'DC').
// Resolves to `response.data` so callers read `.features` directly.
export const getTransmissionLines = async (bbox, filters = {}, limit = 4000, { signal } = {}) => {
  const params = { bbox, limit };
  if (filters.min_voltage != null) params.min_voltage = filters.min_voltage;
  if (filters.volt_class) params.volt_class = filters.volt_class;
  if (filters.line_type) params.line_type = filters.line_type;
  const response = await apiClient.get('/transmission-lines', { params, signal });
  return response.data;
};

// Listing history endpoints
export const getListingHistory = async (parcelId) => {
  const response = await apiClient.get(`/parcels/${parcelId}/listing-history`);
  return response.data;
};

// Saved-screen endpoints (issue #104)
// `params` keys: dom_bucket ('90'|'180'|'365'|'730'),
// sort_by ('largest_price_cut'|'oldest_dom'|'lowest_price_per_acre_stale'),
// state, min_acres, max_price, page, per_page.
export const getStaleAcreageScreen = async (params = {}) => {
  const response = await apiClient.get('/screens/stale-acreage', { params });
  return response.data;
};

// Buildable-retreat screen (issue #106).
// `params` keys: min_score (int 0-100), require_water (bool),
// require_access (bool), exclude_landlocked (bool), state (2-letter),
// page, per_page. Pass `parcel_id` instead for a single-parcel lookup
// (used by the parcel-detail Buildability card) — that bypasses the
// ranking filters and returns whatever the extractor recorded.
export const getBuildableRetreatScreen = async (params = {}) => {
  const response = await apiClient.get('/screens/buildable-retreat', { params });
  return response.data;
};

// Listing media endpoint
export const getListingMedia = async (parcelId) => {
  const response = await apiClient.get(`/parcels/${parcelId}/listing-media`);
  return response.data;
};

// Elevation profile endpoint
export const getElevationProfile = async (parcelId, numPoints = 50) => {
  const response = await apiClient.get(`/parcels/${parcelId}/elevation-profile`, {
    params: { num_points: numPoints },
  });
  return response.data;
};

// Elevation grid (N×N DEM samples for 3D relief rendering on the
// parcel-detail "cake" view — drives the topographic relief on the
// prism's top surface instead of a flat polygon).
export const getElevationGrid = async (parcelId, size = 12) => {
  const response = await apiClient.get(`/parcels/${parcelId}/elevation-grid`, {
    params: { size },
  });
  return response.data;
};

// Export endpoints
export const exportCsv = async (params = {}) => {
  const response = await apiClient.get('/export', {
    params,
    responseType: 'blob',
  });
  return response;
};

// Authenticated CSV/GeoJSON download for the map/search Export button
// (#1142). The /export endpoint sits behind the app-wide login gate
// (#280), so it can no longer be opened via `window.open('/api/v1/export?...')`
// — a bare browser navigation cannot attach the Authorization header and the
// request 401s ("Not Authenticated"). Pull the bytes through apiClient (whose
// request interceptor adds the Bearer token) as a Blob; the caller turns it
// into a client-side download. Empty/null/undefined values are dropped so the
// export URL stays clean. Mirrors downloadParcelsTableCsv.
export const downloadExportCsv = async (params = {}) => {
  const cleaned = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    cleaned[k] = v;
  }
  const response = await apiClient.get('/export', {
    params: cleaned,
    responseType: 'blob',
  });
  return response.data;
};

// Admin/Health endpoints
export const getHealth = async () => {
  const response = await apiClient.get('/admin/health');
  return response.data;
};

export const getConfig = async () => {
  const response = await apiClient.get('/admin/config');
  return response.data;
};

export const updateConfig = async (config) => {
  const response = await apiClient.post('/admin/config', config);
  return response.data;
};

export const getEnrichmentStats = async () => {
  const response = await apiClient.get('/admin/enrichment-stats');
  return response.data;
};

export const getClientErrors = async (limit = 20) => {
  const response = await apiClient.get('/admin/client-errors', { params: { limit } });
  return response.data;
};

// Model-eval dashboard (#787 / INIT-model-eval). Three read-only
// admin-gated endpoints that surface the recurring snapshot written
// by scripts/run_model_eval.py.
export const getModelEvalLatest = async () => {
  const response = await apiClient.get('/admin/model_eval/latest');
  return response.data;
};

export const getModelEvalHistory = async (score, days = 30) => {
  const response = await apiClient.get('/admin/model_eval/history', {
    params: { score, days },
  });
  return response.data;
};

export const getModelEvalAlerts = async (resolved = false) => {
  const response = await apiClient.get('/admin/model_eval/alerts', {
    params: { resolved },
  });
  return response.data;
};

// Notification preferences endpoints
export const getNotificationPreferences = async () => {
  const response = await apiClient.get('/notifications/preferences');
  return response.data;
};

export const updateNotificationPreferences = async (prefs) => {
  const response = await apiClient.put('/notifications/preferences', prefs);
  return response.data;
};

export const getNotificationStats = async () => {
  const response = await apiClient.get('/notifications/stats');
  return response.data;
};

// Comparable parcels endpoint
export const getComparableParcels = async (parcelId, radiusKm = 50, limit = 5) => {
  const response = await apiClient.get(`/parcels/${parcelId}/comps`, {
    params: { radius_km: radiusKm, limit },
  });
  return response.data;
};

// Top opportunities endpoint
export const getTopOpportunities = async (params = {}) => {
  const response = await apiClient.get('/scores/top-opportunities', { params });
  return response.data;
};

// Saved-screen: Big cheap deeded acreage (#102). Returns 3 buckets:
// `clean_candidates`, `needs_acreage_verification`, `likely_false_positives`.
//
// The screen's candidate query is the heaviest read endpoint the
// product has (#1548): it drives from the whole parcels table through
// the view's LATERAL. The backend allows 120s for it, so this request
// must not give up at the shared 30s axios default - the screen would
// then fail in the browser while the backend was still working.
export const getBigCheapAcreage = async (params = {}) => {
  const response = await apiClient.get('/screens/big-cheap-acreage', {
    params,
    timeout: 120000,
  });
  return response.data;
};

// Saved-screen: Mineral-adjacent claim-clean (#105). Returns parcels
// with nearby USGS mines but zero/low BLM mining-claim conflict.
// `commodities` is an array — axios serializes it as repeated params
// matching FastAPI's List[str] query binding.
//
// `total` counts matches among the first `candidate_cap` candidates the
// search checks, not every matching parcel; `truncated` is true when that
// limit was reached (#1784).
//
// The backend gives this route 240s (#1681): its statements may run for
// 90s each and the handler runs two. The request must not give up at the
// shared 30s axios default, or the browser drops the answer the backend
// is now allowed to finish.
export const getMineralAdjacentClaimCleanScreen = async (params = {}) => {
  const response = await apiClient.get(
    '/screens/mineral-adjacent-claim-clean',
    { params, timeout: 240000 },
  );
  return response.data;
};

// Watchlist endpoints
export const createWatchlist = async (data) => {
  const response = await apiClient.post('/watchlists', data);
  return response.data;
};

export const listWatchlists = async () => {
  const response = await apiClient.get('/watchlists');
  return response.data;
};

export const updateWatchlist = async (id, data) => {
  const response = await apiClient.put(`/watchlists/${id}`, data);
  return response.data;
};

export const deleteWatchlist = async (id) => {
  const response = await apiClient.delete(`/watchlists/${id}`);
  return response.data;
};

export const matchWatchlist = async (id) => {
  const response = await apiClient.post(`/watchlists/${id}/match`);
  return response.data;
};

// Time-on-market alert preferences (#794).
// Partial updates only — fields left undefined keep their stored value.
// Returns the full WatchlistResponse with the merged alert_prefs struct.
export const updateWatchlistAlertPrefs = async (id, prefs) => {
  const response = await apiClient.patch(`/watchlists/${id}/alert_prefs`, prefs);
  return response.data;
};

// Auth endpoints
export const login = async (email, password) => {
  // Backend /auth/login expects application/x-www-form-urlencoded with
  // OAuth2 field names (username, password). We send "username" but it
  // contains the email — that's the existing backend contract.
  const body = new URLSearchParams();
  body.append('username', email);
  body.append('password', password);
  const response = await apiClient.post('/auth/login', body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return response.data;  // { access_token, token_type }
};

export const register = async (email, password, fullName) => {
  const response = await apiClient.post('/auth/register', {
    email,
    password,
    full_name: fullName || null,
  });
  return response.data;  // User object
};

export const getCurrentUser = async () => {
  const response = await apiClient.get('/auth/me');
  return response.data;  // User object
};

// Alert endpoints
export const listAlerts = async (params = {}) => {
  const response = await apiClient.get('/alerts', { params });
  return response.data;
};

export const markAlertRead = async (id) => {
  const response = await apiClient.post(`/alerts/${id}/read`);
  return response.data;
};

export const dismissAlert = async (id) => {
  const response = await apiClient.post(`/alerts/${id}/dismiss`);
  return response.data;
};

export const markAllAlertsRead = async (watchlistId = null) => {
  const params = watchlistId ? { watchlist_id: watchlistId } : {};
  const response = await apiClient.post('/alerts/read-all', null, { params });
  return response.data;
};

// Saved-search digest endpoints (#796 / INIT-digest).
// Returns either a populated DigestResponse OR an EmptyDigestResponse
// with empty=true. Both shapes carry a .content key so the UI can
// render them interchangeably.
export const getMyDigest = async (period = 'daily') => {
  const response = await apiClient.get('/digests/me', { params: { period } });
  return response.data;
};

export const getMyDigestHistory = async (period = 'daily', limit = 14) => {
  const response = await apiClient.get('/digests/me/history', {
    params: { period, limit },
  });
  return response.data;
};

// ── System settings (admin) ──────────────────────────────
// Backed by the system_settings table. Used by AdminPanel.
// See backend/src/landai/utils/settings.py.

export const listSystemSettings = async () => {
  const response = await apiClient.get('/admin/settings');
  return response.data;
};

export const updateSystemSetting = async (key, value) => {
  // Use raw axios so the slash-containing key isn't URL-decoded
  // before reaching the path-converter on the backend.
  const response = await apiClient.post(
    `/admin/settings/${encodeURIComponent(key)}`,
    { value },
  );
  return response.data;
};

export const getSystemSettingHistory = async (key, limit = 50) => {
  const response = await apiClient.get(
    `/admin/settings/${encodeURIComponent(key)}/history`,
    { params: { limit } },
  );
  return response.data;
};

// ── Dashboard ────────────────────────────────────────────
// Backend computes these as 5-min cached aggregates over the
// parcels + model_outputs + hazard_risk tables. See
// backend/src/landai/api/v1/dashboard.py.

export const getDashboardTrends = async () => {
  const response = await apiClient.get('/dashboard/trends');
  return response.data;
};

export const getDashboardNews = async ({ days, state, commodity, limit } = {}) => {
  const params = {};
  if (days != null) params.days = days;
  if (state) params.state = state;
  if (commodity) params.commodity = commodity;
  if (limit != null) params.limit = limit;
  const response = await apiClient.get('/dashboard/news', { params });
  return response.data;
};

export const getModelMetrics = async (modelName = null, limit = 50) => {
  const params = { limit };
  if (modelName) params.model_name = modelName;
  const response = await apiClient.get('/dashboard/model-metrics', { params });
  return response.data;
};

// ── Reports ──────────────────────────────────────────────
// The in-app ReportViewer modal lists available report templates
// via /reports/templates and fetches HTML via /parcels/{id}/report.html
// (the same endpoint the legacy "Generate Report" external link used,
// just rendered inline via an iframe srcDoc instead of a new tab).

export const listReportTemplates = async () => {
  const response = await apiClient.get('/reports/templates');
  return response.data; // [{ id, label }, ...]
};

export const getParcelReportHtml = async (parcelId, template = 'buyer_brief') => {
  // Returns the raw HTML string. We bypass the JSON Content-Type by
  // passing responseType: 'text' so axios doesn't try to parse it.
  const response = await apiClient.get(
    `/parcels/${encodeURIComponent(parcelId)}/report.html`,
    { params: { template }, responseType: 'text' },
  );
  return response.data;
};

// AI buyer-brief narrative (#35). The report HTML renders with a
// placeholder; the viewer fires this in parallel and patches the
// narrative in when it returns. Cache hit: ~50ms. Cache miss: ~8s.
export const getParcelAiSummary = async (parcelId, { signal } = {}) => {
  const response = await apiClient.get(
    `/parcels/${encodeURIComponent(parcelId)}/ai_summary`,
    { signal },
  );
  return response.data;
};

// NOTE: the legacy `triggerParcelAiResearch` (POST /parcels/:id/ai_research
// inline web_search fan-out, the "Research the web…" button) was retired in
// #1093. It routinely dead-ended and is superseded by the on-demand
// parcelmax Deep-research flow — see `startDeepResearch` above and the
// /parcel/:id/research view.

// Server-side report URL — used as the "Open in new tab" fallback
// inside the viewer modal so users can still print or share the
// report via the browser's native chrome.
export const reportHtmlUrl = (parcelId, template = 'buyer_brief') => {
  const base = process.env.REACT_APP_API_URL || DEFAULT_API_BASE;
  const params = new URLSearchParams({ template });
  return `${base}/parcels/${encodeURIComponent(parcelId)}/report.html?${params.toString()}`;
};

// Subsurface signal endpoint (#831 Lane 5 — "What's beneath your parcel").
// Returns geophysics / geology / geochemistry / radiometric / heat_flow
// sections, each with per-feature { value, pct } objects where pct is the
// CONUS percentile rank (0-100).  Sections with no data are empty objects.
export const getParcelSubsurface = async (parcelId) => {
  const response = await apiClient.get(`/parcels/${encodeURIComponent(parcelId)}/subsurface`);
  return response.data;
};

export default apiClient;
